/**
 * Analytics consent banner (opt-in).
 * Shown when user has not yet chosen whether to allow analytics.
 * Accept = enable PostHog and init; Decline = no tracking. Choice persisted in localStorage.
 */

import React, { useState } from "react";
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  initProductAnalytics,
  getPostHogKey,
} from "../../config/productAnalytics";

export function AnalyticsConsentBanner(): React.ReactElement | null {
  const [consent, setConsentState] = useState<"true" | "false" | null>(() =>
    getAnalyticsConsent()
  );
  const [dismissed, setDismissed] = useState(false);

  const show = getPostHogKey() && consent === null && !dismissed;
  if (!show) return null;

  const handleAccept = (): void => {
    setAnalyticsConsent(true);
    initProductAnalytics();
    setConsentState("true");
    setDismissed(true);
  };

  const handleDecline = (): void => {
    setAnalyticsConsent(false);
    setConsentState("false");
    setDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-label="Analytics consent"
      className="fixed bottom-0 left-0 right-0 z-[9999] flex flex-col gap-3 border-t border-gray-200 bg-white p-4 shadow-lg sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <p className="text-sm sm:text-base text-gray-700">
        We use anonymous analytics to improve the product (e.g. feature usage,
        performance). No personal data is shared with third parties. You can
        change your mind anytime in settings.
      </p>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={handleDecline}
          className="min-h-[44px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={handleAccept}
          className="min-h-[44px] rounded-md border border-transparent bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation"
        >
          Accept
        </button>
      </div>
    </div>
  );
}
